/**
 * `AudioGenerationPort` — the provider-neutral contract for generated music and
 * sound effects (plan row 1.1, ADR 0009).
 *
 * Creator-facing surfaces depend on this module alone. No vendor name, model id
 * or request id appears in a creator-facing API, a job payload or a render
 * manifest: they exist only in {@link AudioGeneratorProvenanceV1}, which the
 * pipeline stamps on the candidate it produces.
 *
 * ## The music brief cannot carry creation context (ADR 0009 Q1, plan row 1.10)
 *
 * The owner accepted Lyria knowing that creator briefs are provider training
 * input, on the condition that a brief is bounded to reviewed musical
 * direction. The closed fields may be machine-prefilled and creator-edited,
 * but the game's source, design document, accepted concept, title and
 * asset inventory are never sent. That condition is enforced HERE, structurally,
 * not by convention downstream:
 *
 *  - {@link validateAudioGenerationRequestV1} is every adapter's first line, and
 *    it rejects any key it does not know. There is no free-text pass-through and
 *    no `context`, `source`, `design`, `title` or `extra` field to smuggle one
 *    through, so an adapter cannot be handed a design doc.
 *  - Every brief field is one bounded line, and {@link renderAudioBriefText} is
 *    the ONLY text that may reach a provider — it is derived from the validated
 *    brief fields and nothing else.
 *
 * Stated honestly: analysis may propose these bounded values, but this
 * guarantees the PLATFORM never forwards raw creation material. If a future
 * proposal wants design or concept text in the provider payload, ADR 0009 Q1
 * re-opens and Vertex is evaluated first — it is not a validator tweak.
 *
 * The one field added since (plan row 1.8): `avoid`, the creator's saved
 * "avoid this audio direction" preference (`Creation.avoidDirection.audio`),
 * folded in by the kickoff — never supplied by a client. It is creator-authored
 * musical direction like every other field, so Q1's bound is unchanged.
 */
import { PlatformError, type AudioMediaKind } from '@djgg/core';
import { MAX_AVOID_DIRECTION_CHARS } from './avoidDirection.js';

/** Audio a provider may be asked for. Mirrors the media kinds exactly — there
 *  is no third generated audio category. */
export type AudioGenerationKind = AudioMediaKind;

/** Adopted spend circuit breakers (ADR 0009 Q5). Automatic Deej-led music
 * reserves one unit atomically with Build acceptance. */
export const MUSIC_GENERATION_CAPS = Object.freeze({
  perCreationLifetime: 10,
  perCreatorDay: 20,
  platformPerDay: 200,
});

/** Reserved quota identity for the platform-wide daily music circuit breaker.
 * Kept beside the caps so automatic and explicit generation cannot drift onto
 * different counters. It is never a creator identity. */
export const PLATFORM_MUSIC_QUOTA_UID = '__platform_music__';

/** How bespoke an SFX should sound. A closed vocabulary, so the character of a
 *  cue can never become a sentence about the game. */
export const SFX_CHARACTERS = ['natural', 'cartoon', 'cinematic', 'retro'] as const;
export type SfxCharacter = (typeof SFX_CHARACTERS)[number];

/**
 * Creator-authored MUSICAL direction — the whole of what may reach a music
 * provider. Vocals are never requested (the plan excludes them), so the
 * renderer states "instrumental only" unconditionally rather than offering a
 * field that could ask for them.
 */
export interface MusicBriefV1 {
  /** Feel, one line: `tense synth chase`. */
  readonly mood: string;
  /** Style, one line: `retro synthwave`. */
  readonly genre: string;
  /** Beats per minute. */
  readonly tempoBpm: number;
  /** 1..{@link MAX_INSTRUMENTS} instrument names. */
  readonly instrumentation: readonly string[];
  /** Optional reference FEEL — never a named artist or track (providers block
   *  artist-voice and copyrighted-lyric prompts, and the plan excludes
   *  copyrighted-style prompting). */
  readonly referenceFeel?: string;
  /** The creator's saved "avoid this audio direction" line, folded in by the
   *  kickoff from `Creation.avoidDirection.audio`. Steering, not a guarantee. */
  readonly avoid?: string;
}

/** Creator-authored direction for one bespoke sound effect. */
export interface SfxBriefV1 {
  /** What the sound IS, one line: `short metallic clang`. */
  readonly description: string;
  readonly character: SfxCharacter;
  /** See {@link MusicBriefV1.avoid}. */
  readonly avoid?: string;
}

/** A brief tagged with the kind it belongs to — the shape every caller passes
 *  around so `kind` and `brief` can never drift apart or need a cast. */
export type AudioBriefRefV1 =
  | { readonly kind: 'music'; readonly brief: MusicBriefV1 }
  | { readonly kind: 'sfx'; readonly brief: SfxBriefV1 };

/** One bounded generation request. `targetDurationMs` keeps the port away from
 *  unbounded streaming, which ADR 0009 leaves out of scope. */
export interface MusicGenerationRequestV1 {
  readonly kind: 'music';
  readonly brief: MusicBriefV1;
  readonly targetDurationMs: number;
}
export interface SfxGenerationRequestV1 {
  readonly kind: 'sfx';
  readonly brief: SfxBriefV1;
  readonly targetDurationMs: number;
}
export type AudioGenerationRequestV1 = MusicGenerationRequestV1 | SfxGenerationRequestV1;

/** Per-call options. Cancellation is the caller's, so a cancelled creator
 *  request can abandon an in-flight provider call. */
export interface AudioGenerationOptionsV1 {
  readonly signal?: AbortSignal;
}

/** Vendor identity — recorded on the candidate, never exposed as an input. */
export interface AudioGeneratorProvenanceV1 {
  readonly provider: string;
  readonly model: string;
  readonly requestId?: string;
}

/**
 * The containers a provider reply may carry across this port. An adapter
 * reports the mime it OBSERVED on the wire and nothing else; it never converts.
 * Which of these a given kind can ingest is the worker's decision, in one
 * place: music is decoded and re-encoded whatever it arrives in, so it accepts
 * both; SFX is stored exactly as returned, so it accepts canonical WAV only.
 *
 * `audio/mpeg` is here because the row 1.9 smoke (2026-09-22) showed it is the
 * ONLY output `lyria-3.5` produces on the Interactions API — `audio/wav` and
 * `audio/ogg_opus` are both refused with 400. The cost is real and recorded
 * where the decoder lives (`mp3Source.ts`): the provider's MP3 is lossy, and
 * the stored Opus loop is a second lossy generation on top of it.
 */
export const AUDIO_GENERATION_SOURCE_MIMES = ['audio/wav', 'audio/mpeg'] as const;
export type AudioGenerationSourceMime = (typeof AUDIO_GENERATION_SOURCE_MIMES)[number];

export function isAudioGenerationSourceMime(value: unknown): value is AudioGenerationSourceMime {
  return (AUDIO_GENERATION_SOURCE_MIMES as readonly unknown[]).includes(value);
}

/**
 * What an adapter returns: the exact provider bytes, unmodified, plus the
 * container it observed and the provenance to record.
 *
 * An adapter never re-encodes. Re-encoding belongs to one place — the worker's
 * music post-processing, whose output is the compressed version-owned
 * artifact — and an adapter that transcoded would be a second encoder.
 */
export interface AudioGenerationResultV1 {
  readonly bytes: Uint8Array;
  readonly mime: AudioGenerationSourceMime;
  readonly provenance: AudioGeneratorProvenanceV1;
}

/**
 * The provider-agnostic generator. One port instance serves exactly one
 * capability: Lyria generates music; Gemini proposes bounded sfxr parameters
 * and the platform renderer generates SFX. Neither pretends to the other.
 */
export interface AudioGenerationPort {
  readonly capability: AudioGenerationKind;
  /** Provenance-only generator tag (`lyria`, `gemini-sfxr`, `fake`). */
  readonly provider: string;
  /** Provenance-only model id. */
  readonly model: string;
  generate(
    request: AudioGenerationRequestV1,
    opts?: AudioGenerationOptionsV1,
  ): Promise<AudioGenerationResultV1>;
}

/** Whether generated audio is offered at all, per kind. Both default OFF: the
 *  release gate for plan row 1.1 is that provider flags ship off and the
 *  upload/curated paths work with them off. */
export interface AudioGenerationFlags {
  readonly music: boolean;
  readonly sfx: boolean;
}

/** The shipped default — no provider is offered until a superadmin turns one on
 *  AND the builder carries the deployment opt-in. */
export const AUDIO_GENERATION_DISABLED: AudioGenerationFlags = Object.freeze({
  music: false,
  sfx: false,
});

/** The ONE decision of "may a creator ask for generated audio of this kind". */
export function audioGenerationEnabled(flags: AudioGenerationFlags, kind: AudioGenerationKind): boolean {
  return flags[kind] === true;
}

/** Request bounds per kind, aligned with `AUDIO_MEDIA_FORMATS` durations so a
 *  request can never ask for audio the ingestion gate must then reject. */
export const AUDIO_GENERATION_BOUNDS: Readonly<Record<AudioGenerationKind, { minDurationMs: number; maxDurationMs: number }>> =
  Object.freeze({
    music: Object.freeze({ minDurationMs: 5_000, maxDurationMs: 120_000 }),
    sfx: Object.freeze({ minDurationMs: 200, maxDurationMs: 5_000 }),
  });

const MAX_LINE = 80;
const MAX_REFERENCE_LINE = 120;
const MAX_INSTRUMENT_LINE = 40;
/** Instruments per brief — a list, never an inventory. */
export const MAX_INSTRUMENTS = 6;
const MIN_TEMPO_BPM = 40;
const MAX_TEMPO_BPM = 200;
/** Ceiling on the rendered provider text. A design document cannot fit. */
export const MAX_RENDERED_BRIEF_CHARS = 320;

function line(value: unknown, path: string, max = MAX_LINE): string {
  if (typeof value !== 'string') throw new PlatformError('validation', `${path} must be text`);
  const trimmed = value.trim();
  if (!trimmed) throw new PlatformError('validation', `${path} must not be empty`);
  if (trimmed.length > max) {
    throw new PlatformError('validation', `${path} must be at most ${max} characters of musical direction`);
  }
  // One line, no control characters: a brief is direction, not a pasted
  // document. Newlines are the cheapest structural tell of the latter.
  if (/[\u0000-\u001f\u007f]/.test(trimmed)) {
    throw new PlatformError('validation', `${path} must be a single line of direction`);
  }
  return trimmed;
}

function onlyKeys(value: Record<string, unknown>, allowed: readonly string[], path: string): void {
  const extra = Object.keys(value).filter((key) => !allowed.includes(key));
  if (extra.length > 0) {
    throw new PlatformError(
      'validation',
      `${path} accepts only ${allowed.join(', ')} — creation context (${extra.join(', ')}) is never sent to an audio provider`,
    );
  }
}

function object(value: unknown, path: string): Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    throw new PlatformError('validation', `${path} must be an object`);
  }
  return value as Record<string, unknown>;
}

/** Validate a MUSIC brief: closed keys, bounded one-line direction. */
export function validateMusicBriefV1(value: unknown): MusicBriefV1 {
  const v = object(value, 'musicBrief');
  onlyKeys(v, ['mood', 'genre', 'tempoBpm', 'instrumentation', 'referenceFeel', 'avoid'], 'musicBrief');
  if (!Number.isInteger(v.tempoBpm) || (v.tempoBpm as number) < MIN_TEMPO_BPM || (v.tempoBpm as number) > MAX_TEMPO_BPM) {
    throw new PlatformError('validation', `musicBrief.tempoBpm must be an integer from ${MIN_TEMPO_BPM} to ${MAX_TEMPO_BPM}`);
  }
  if (!Array.isArray(v.instrumentation) || v.instrumentation.length < 1 || v.instrumentation.length > MAX_INSTRUMENTS) {
    throw new PlatformError('validation', `musicBrief.instrumentation must list 1..${MAX_INSTRUMENTS} instruments`);
  }
  return {
    mood: line(v.mood, 'musicBrief.mood'),
    genre: line(v.genre, 'musicBrief.genre'),
    tempoBpm: v.tempoBpm as number,
    instrumentation: v.instrumentation.map((entry, index) =>
      line(entry, `musicBrief.instrumentation[${index}]`, MAX_INSTRUMENT_LINE)),
    ...(v.referenceFeel === undefined
      ? {}
      : { referenceFeel: line(v.referenceFeel, 'musicBrief.referenceFeel', MAX_REFERENCE_LINE) }),
    ...(v.avoid === undefined ? {} : { avoid: line(v.avoid, 'musicBrief.avoid', MAX_AVOID_DIRECTION_CHARS) }),
  };
}

/** Parse the compact, creator-visible direction stored on GameDesign. This is
 * deliberately a closed formatter contract rather than free-text inference:
 * only the four disclosed musical fields can become a provider request. */
export function parseApprovedMusicDirection(value: string | undefined): MusicBriefV1 | null {
  if (!value) return null;
  const fields = new Map<string, string>();
  for (const part of value.split(/\s*[;·|]\s*/)) {
    const match = /^([^:]+):\s*(.+)$/.exec(part.trim());
    if (match) fields.set(match[1]!.trim().toLowerCase(), match[2]!.trim());
  }
  const tempo = /^(\d{2,3})\s*bpm$/i.exec(fields.get('tempo') ?? '');
  const instrumentation = (fields.get('instruments') ?? '').split(',').map((item) => item.trim()).filter(Boolean);
  if (!fields.get('mood') || !fields.get('genre') || !tempo || instrumentation.length === 0) return null;
  try {
    return validateMusicBriefV1({
      mood: fields.get('mood'),
      genre: fields.get('genre'),
      tempoBpm: Number(tempo[1]),
      instrumentation,
    });
  } catch {
    return null;
  }
}

/** The single compact representation reviewed in the wizard and parsed by the
 * server. Keeping this beside the parser prevents UI punctuation from silently
 * disabling the approved music branch. */
export function formatApprovedMusicDirection(brief: MusicBriefV1): string {
  const validated = validateMusicBriefV1(brief);
  return [
    `Mood: ${validated.mood}`,
    `genre: ${validated.genre}`,
    `tempo: ${validated.tempoBpm} BPM`,
    `instruments: ${validated.instrumentation.join(', ')}`,
  ].join(' · ').slice(0, 300);
}

/** Validate an SFX brief: one bounded description plus a closed character. */
export function validateSfxBriefV1(value: unknown): SfxBriefV1 {
  const v = object(value, 'sfxBrief');
  onlyKeys(v, ['description', 'character', 'avoid'], 'sfxBrief');
  if (typeof v.character !== 'string' || !SFX_CHARACTERS.includes(v.character as SfxCharacter)) {
    throw new PlatformError('validation', `sfxBrief.character must be one of ${SFX_CHARACTERS.join(', ')}`);
  }
  return {
    description: line(v.description, 'sfxBrief.description'),
    character: v.character as SfxCharacter,
    ...(v.avoid === undefined ? {} : { avoid: line(v.avoid, 'sfxBrief.avoid', MAX_AVOID_DIRECTION_CHARS) }),
  };
}

/**
 * Fold the creation's saved audio preference into a validated brief. The ONE
 * place `avoid` enters a brief: the kickoff calls it after loading the
 * creation, and a client-supplied `avoid` is refused there, so the stored job
 * and the provider text always reflect the preference as saved.
 */
export function withAvoidDirection<B extends MusicBriefV1 | SfxBriefV1>(brief: B, avoid: string | undefined): B {
  const { avoid: _dropped, ...rest } = brief;
  return (avoid === undefined ? rest : { ...rest, avoid }) as B;
}

/**
 * The PORT BOUNDARY. Every adapter calls this first, so the "no creation
 * context" condition holds even for a caller that skipped the brief validator.
 */
export function validateAudioGenerationRequestV1(value: unknown): AudioGenerationRequestV1 {
  const v = object(value, 'audioGenerationRequest');
  onlyKeys(v, ['kind', 'brief', 'targetDurationMs'], 'audioGenerationRequest');
  if (v.kind !== 'music' && v.kind !== 'sfx') {
    throw new PlatformError('validation', 'audioGenerationRequest.kind must be music or sfx');
  }
  const kind = v.kind;
  const bounds = AUDIO_GENERATION_BOUNDS[kind];
  if (!Number.isInteger(v.targetDurationMs) ||
    (v.targetDurationMs as number) < bounds.minDurationMs ||
    (v.targetDurationMs as number) > bounds.maxDurationMs) {
    throw new PlatformError(
      'validation',
      `audioGenerationRequest.targetDurationMs must be an integer from ${bounds.minDurationMs} to ${bounds.maxDurationMs} for ${kind}`,
    );
  }
  const targetDurationMs = v.targetDurationMs as number;
  return kind === 'music'
    ? { kind, brief: validateMusicBriefV1(v.brief), targetDurationMs }
    : { kind, brief: validateSfxBriefV1(v.brief), targetDurationMs };
}

/** An adapter serves one capability; handing it the other kind is a caller bug,
 *  not a provider failure. */
export function assertAudioCapability(port: AudioGenerationPort, request: AudioGenerationRequestV1): void {
  if (port.capability !== request.kind) {
    throw new PlatformError(
      'validation',
      `${port.provider} generates ${port.capability}, not ${request.kind}`,
    );
  }
}

/** The ONE music text a provider may see, derived from validated brief fields. */
export function renderMusicBriefText(brief: MusicBriefV1): string {
  const parts = [
    brief.mood,
    brief.genre,
    `${brief.instrumentation.join(', ')}`,
    `${brief.tempoBpm} bpm`,
    ...(brief.referenceFeel ? [brief.referenceFeel] : []),
    ...(brief.avoid ? [`avoid ${brief.avoid}`] : []),
    'instrumental only, no vocals',
  ];
  return assertRenderedLength(parts.join('; '));
}

/** The ONE SFX text a provider may see. */
export function renderSfxBriefText(brief: SfxBriefV1): string {
  return assertRenderedLength(
    [
      brief.description,
      `${brief.character} sound effect`,
      ...(brief.avoid ? [`avoid ${brief.avoid}`] : []),
      'no speech, no music',
    ].join('; '),
  );
}

/**
 * A provider DECLINED the brief (its safety or content filter) — ADR 0009 Q6:
 * this reaches the creator as a refusal with the provider's reason, never as a
 * generic failure. Encoded as a stable prefix on the job's terminal `error`,
 * the same shape as a creator cancellation (`assetGenCancelReason`), so no
 * fifth status value and no schema change is needed to tell the two apart.
 */
export const AUDIO_BRIEF_REFUSED_PREFIX = 'the provider declined this brief: ';

/** The terminal reason for a refused brief; `detail` is the provider's own text. */
export function audioBriefRefusal(detail: string): string {
  return `${AUDIO_BRIEF_REFUSED_PREFIX}${detail.trim() || 'no reason given'}`;
}

/** The provider's reason when a terminal error is a refusal; null otherwise. */
export function audioBriefRefusalDetail(error: string | null | undefined): string | null {
  return markedDetail(AUDIO_BRIEF_REFUSED_PREFIX, error);
}

/** The audio provider could not answer: a rate limit, a server fault, a
 * timeout or a network failure that outlived the transport's attempts. Not a
 * statement about the brief; asking again later may succeed. */
export const AUDIO_PROVIDER_UNAVAILABLE_PREFIX = 'the audio provider was unavailable: ';
export function audioProviderUnavailable(detail: string): string {
  return `${AUDIO_PROVIDER_UNAVAILABLE_PREFIX}${detail.trim() || 'no reason given'}`;
}
export function audioProviderUnavailableDetail(error: string | null | undefined): string | null {
  return markedDetail(AUDIO_PROVIDER_UNAVAILABLE_PREFIX, error);
}

/** The audio provider rejected a request of OUR making (a 4xx that is neither
 * a credential failure nor a content refusal): a platform bug, never the
 * creator's brief. */
export const AUDIO_REQUEST_REJECTED_PREFIX = 'the audio provider rejected the platform request: ';
export function audioRequestRejected(detail: string): string {
  return `${AUDIO_REQUEST_REJECTED_PREFIX}${detail.trim() || 'no reason given'}`;
}
export function audioRequestRejectedDetail(error: string | null | undefined): string | null {
  return markedDetail(AUDIO_REQUEST_REJECTED_PREFIX, error);
}

function markedDetail(prefix: string, error: string | null | undefined): string | null {
  return error && error.startsWith(prefix) ? error.slice(prefix.length) : null;
}

/** Render whichever brief the kind selects (one renderer per kind, one caller
 *  per adapter — the stored job prompt and the provider text are the same
 *  string by construction, never two formattings of one brief). Takes the
 *  tagged pair, so the kind narrows the brief and no cast is needed. */
export function renderAudioBriefText(ref: AudioBriefRefV1): string {
  return ref.kind === 'music' ? renderMusicBriefText(ref.brief) : renderSfxBriefText(ref.brief);
}

function assertRenderedLength(text: string): string {
  if (text.length > MAX_RENDERED_BRIEF_CHARS) {
    throw new PlatformError('validation', `rendered brief exceeds ${MAX_RENDERED_BRIEF_CHARS} characters`);
  }
  return text;
}
